/** @format */
import { isNil, negate, pickBy } from 'lodash';
import Script from 'next/script';
import React, {
    FunctionComponent,
    useContext,
    useEffect,
    useMemo,
} from 'react';

import { getMappedIntercomMarket } from '@toucan/common/helpers/intercom-market';

import { logger } from '@toucan/client/global/application';
import { useFeatureFlag } from '@toucan/client/global/feature-flag';
import { executeIntercomCommand } from '@toucan/client/global/intercom';
import { MarketContext } from '@toucan/client/global/market';

import { getPublicIntercomConfig } from './helpers';
import { useAutoLoadIntercom } from './hooks/use-auto-load-intercom';
import { useIdentityVerification } from './hooks/use-identity-verification';
import { IntercomScriptProps } from './types';

export const IntercomScript: FunctionComponent<IntercomScriptProps> = ({
    enabled: manualEnabled = false,
    verticalPadding = 20,
    onLoadCommand = { type: 'show' },
}) => {
    const { activeMarket } = useContext(MarketContext);

    const isConsolidatedIntercom = Boolean(
        useFeatureFlag('APE-3584-(consolidate-intercom-workspaces)')?.isEnabled
    );

    const intercomConfig = getPublicIntercomConfig(isConsolidatedIntercom);

    const { workspaceId, baseDomain = 'intercom.io' } =
        intercomConfig?.[getMappedIntercomMarket(activeMarket.code)] ?? {};

    const baseApiUrl = new URL(`https://api-iam.${baseDomain}`).href.replace(
        /\/$/,
        ''
    );

    // We lazily load the Intercom script on first activation unless we are
    // on specific pages
    const shouldAutoLoadScript = useAutoLoadIntercom();

    const shouldLoadScript = manualEnabled || shouldAutoLoadScript;

    const identityVerification = useIdentityVerification({
        enabled: shouldLoadScript,
        includeContactInformation: manualEnabled,
    });

    if (activeMarket.code && !workspaceId) {
        logger.error('Intercom workspaceId not configured for market');
        logger.addLabels({ mpb_market: activeMarket.code });
    }

    const intercomScriptSrc = workspaceId
        ? `https://widget.intercom.io/widget/${workspaceId}`
        : undefined;

    const intercomSettings = useMemo(() => {
        return {
            app_id: workspaceId,
            api_base: baseApiUrl,
            hide_default_launcher: true,
            custom_launcher_selector: '.intercom-trigger',
            language_override: activeMarket.language,
            vertical_padding: verticalPadding,
            ...(!identityVerification.isSuccess
                ? {}
                : pickBy(identityVerification.data, negate(isNil))),
        };
    }, [
        workspaceId,
        baseApiUrl,
        activeMarket.language,
        verticalPadding,
        identityVerification.isSuccess,
        identityVerification.data,
    ]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
        const win: any = window;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
        const intercom = win.Intercom;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
        win.intercomSettings = {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            ...win.intercomSettings,
            ...intercomSettings,
        };

        // If Intercom is active, update settings
        if (typeof intercom === 'function') {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Legacy code: do not use as an example.
            intercom('reattach_activator');
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            intercom('update', win.intercomSettings);
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
            const intercomPlaceholder: any = function () {
                // eslint-disable-next-line prefer-rest-params, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
                intercomPlaceholder.c(arguments);
            };

            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            intercomPlaceholder.q = [] as any[];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            intercomPlaceholder.c = function (args: any) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
                intercomPlaceholder.q.push(args);
            };
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            win.Intercom = intercomPlaceholder;
        }
    }, [intercomSettings]);

    if (!shouldLoadScript) {
        return null;
    }

    return (
        <Script
            id="next-script-intercom"
            data-testid="intercom-script"
            strategy="afterInteractive"
            src={intercomScriptSrc}
            onLoad={() => {
                if (manualEnabled) {
                    executeIntercomCommand(onLoadCommand);
                }
            }}
        />
    );
};
